import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import type { AuthClient } from './client';
import type { AuthResult, AuthState, MfaMethod, Session } from './machine';

// React binding for the external auth client. The same live object is consumed
// imperatively by TanStack Router and reactively through `useAuth()`.

export interface SignInCredentials {
  email: string;
  password: string;
}

export interface MfaVerifyInput {
  code: string;
  method: MfaMethod;
}

export interface ChangePasswordInput {
  oldPassword: string;
  newPassword: string;
}

/** The backend-facing side of auth: performs the call, returns a machine result. */
export interface AuthOperations {
  signIn(credentials: SignInCredentials): Promise<AuthResult>;
  verifyMfa(input: MfaVerifyInput & { mfaToken: string }): Promise<AuthResult>;
  resendMfa(mfaToken: string): Promise<void>;
  changePassword(input: ChangePasswordInput & { flowToken: string }): Promise<AuthResult>;
  signOut(): Promise<void>;
  restore(): Promise<Session | null>;
}

/** Imperative router-facing API; each method drives the reducer through its flow. */
export interface AuthApi {
  signIn(credentials: SignInCredentials): Promise<void>;
  verifyMfa(input: MfaVerifyInput): Promise<void>;
  resendMfa(): Promise<void>;
  changePassword(input: ChangePasswordInput): Promise<void>;
  signOut(): Promise<void>;
  restore(): Promise<void>;
  /** Called by the http layer's 401 policy when refresh fails (03-05 §4). */
  sessionExpired(): void;
}

export interface AuthContextValue {
  state: AuthState;
  auth: AuthClient;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  auth: AuthClient;
  children: ReactNode;
}

export function AuthProvider({ auth, children }: AuthProviderProps) {
  const subscribe = useCallback((listener: () => void) => auth.subscribe(listener), [auth]);
  const state = useSyncExternalStore(
    subscribe,
    () => auth.state,
    () => auth.state,
  );
  return createElement(AuthContext.Provider, { value: { state, auth } }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an <AuthProvider>');
  return ctx;
}
